import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Skeleton, message } from 'antd';
import { I18n } from '@aws-amplify/core';

import { REPORT_NAME_KEYS } from '@/utils/constants';
import { useBeraReportV2 } from '@/context/BeraReportV2';
import { useBeraSteps, useCustomReport } from '@/components/views/BeraJob/hooks/hooks';
import { useGetBeraReportV2 } from '@/hooks/useGetBeraReportV2';
import { BeraStepKey } from '@/components/views/BeraJob/BeraFormSteps/BeraStep/BeraStepKey';

import { Footer } from './Footer';
import { Result } from './Result';
import { Stepper } from './Stepper';
import { Informations } from './Informations';
import { buildCheckedStepKeyFieldPaths, buildInitialValues } from './utils';
import { useEvaluationSteps, useSaveEvaluation } from './_hooks';
import { Container, StepKeyWrapper } from './styles';

import type { BeraReportV2 } from '@/types/Bera';
import type { EvaluationStep, InformationsValues, StepFormValues } from './types';

const { useForm, useWatch } = Form;

const FORM_NAME = 'bera_report';
const NO_RISK_FIELD = 'has_no_risk';

const CONSOLIDATED_SEQUENCE = Number.MAX_SAFE_INTEGER;

const INFORMATIONS_STEP = 0;

interface EvaluationProps {
	report: BeraReportV2;
	onFinished: () => void;
}

export function Evaluation({ report, onFinished }: Readonly<EvaluationProps>): JSX.Element {
	const [form] = useForm();

	const { file, jobSummary, organizationId, companyId, onUnsavedNoRiskRemovalChange, onNoRiskSaved } = useBeraReportV2();

	const [anyChecked, setAnyChecked] = useState<boolean>(false);

	const details = useGetBeraReportV2({
		id: report.id,
		organization_id: organizationId,
		company_id: companyId
	});

	const { data: customReport } = useCustomReport({
		organization_id: organizationId,
		company_id: companyId,
		reportName: REPORT_NAME_KEYS.BERA
	});

	const customReportId = file.CustomReportsFiles?.custom_report_id ?? customReport?.id;

	const stepsList = useBeraSteps({
		organization_id: organizationId,
		company_id: companyId,
		custom_report_id: customReportId
	});

	const savedStep = details.isFetching ? undefined : details.data?.step;

	const isConsolidated = details.data?.consolidated ?? false;

	const hasSavedNoRisk = details.data?.has_no_risk ?? false;

	const hasWatchedNoRisk: boolean | undefined = useWatch([file.id, NO_RISK_FIELD], { form, preserve: true });

	const hasNoRisk = hasWatchedNoRisk ?? hasSavedNoRisk;

	const hadNoRiskRef = useRef<boolean>(hasNoRisk);

	const hasUnsavedNoRiskRemoval = hasSavedNoRisk && hasWatchedNoRisk === false;

	const isLocked = isConsolidated || hasNoRisk;

	let initialSequence = savedStep;

	let reachedSequence = details.isFetching ? undefined : details.data?.reached_step;

	if (isLocked) {
		initialSequence = CONSOLIDATED_SEQUENCE;
		reachedSequence = CONSOLIDATED_SEQUENCE;
	}

	const {
		steps,
		currentStep,
		furthestStep,
		isComplete,
		currentStepData,
		isFirstStep,
		isResultStep,
		lastStep,
		nextStep,
		previousStep,
		goTo,
		goPrevious,
		rewindProgress,
		sequenceAt
	} = useEvaluationSteps(stepsList.data, initialSequence, reachedSequence);

	useEffect(() => {
		const wasNoRisk = hadNoRiskRef.current;

		hadNoRiskRef.current = hasNoRisk;

		if (!wasNoRisk || hasNoRisk || isConsolidated) {
			return;
		}

		rewindProgress();
	}, [hasNoRisk]);

	useEffect(() => {
		onUnsavedNoRiskRemovalChange(hasUnsavedNoRiskRemoval);
	}, [hasUnsavedNoRiskRemoval]);

	useEffect(() => {
		return () => onUnsavedNoRiskRemovalChange(false);
	}, []);

	const { isSaving, currentStepResultId, saveInformations, saveStep, saveStepKey, savePendingStepKeys } =
		useSaveEvaluation({
			organizationId,
			companyId,
			reportId: report.id,
			fileId: file.id,
			form
		});

	const initialValues = useMemo(
		() => buildInitialValues({ fileId: file.id, report, details: details.data }),
		[file.id, report, details.data]
	);

	const isLoading = details.isLoading || stepsList.isLoading;

	const isError = details.isError || stepsList.isError;

	useEffect(() => {
		if (!details.data || form.isFieldsTouched()) {
			return;
		}

		form.resetFields();
	}, [details.data, form]);

	async function navigateTo(step: number): Promise<void> {
		const reachedStep = goTo(step);

		const sequence = sequenceAt(reachedStep);

		if (isLocked || sequence === undefined || reachedStep === currentStep) {
			return;
		}

		await saveStep(sequence);
	}

	const isStepKeyStep = !isFirstStep && !isResultStep && !!currentStepData;

	async function validateCheckedStepKeys(step: EvaluationStep): Promise<boolean> {
		const values: StepFormValues | undefined = form.getFieldValue([file.id, step.id]);
		const fieldPaths = buildCheckedStepKeyFieldPaths({ fileId: file.id, step, values });

		if (!fieldPaths.length) {
			return true;
		}

		try {
			await form.validateFields(fieldPaths);

			return true;
		} catch {
			message.error(I18n.get('Fill in the fields of the selected risk factors.'));

			return false;
		}
	}

	async function canLeaveCurrentStep(): Promise<boolean> {
		if (!isStepKeyStep) {
			return true;
		}

		const result = await validateCheckedStepKeys(currentStepData);

		return result;
	}

	async function handleStepClick(step: number): Promise<void> {
		const canLeave = await canLeaveCurrentStep();

		if (!canLeave) {
			return;
		}

		await navigateTo(step);
	}

	async function handleAnyCheckedChange(checked: boolean): Promise<void> {
		setAnyChecked(checked);
	}

	async function validateInformations(): Promise<boolean> {
		try {
			await form.validateFields();

			return true;
		} catch {
			return false;
		}
	}

	async function handleNextFromInformations(): Promise<void> {
		const isValid = await validateInformations();

		if (!isValid) {
			return;
		}

		const values: InformationsValues = form.getFieldValue([file.id]);

		await saveInformations(values);

		const hasNoRiskChanged = !!values.has_no_risk !== hasSavedNoRisk;

		if (hasNoRiskChanged) {
			onNoRiskSaved();
		}

		if (values.has_no_risk) {
			goTo(lastStep);
			return;
		}

		await navigateTo(nextStep);
	}

	async function handleNextFromStepKeys(): Promise<void> {
		if (!currentStepData) {
			return;
		}

		if (!anyChecked) {
			message.error(I18n.get('Select at least one option.'));
			return;
		}

		const isValid = await validateCheckedStepKeys(currentStepData);

		if (!isValid) {
			return;
		}

		await savePendingStepKeys(currentStepData);

		setAnyChecked(false);
		await navigateTo(nextStep);
	}

	async function handleNext(): Promise<void> {
		if (isFirstStep) {
			await handleNextFromInformations();
			return;
		}

		await handleNextFromStepKeys();
	}

	function handleResultPrevious(): void {
		if (isLocked) {
			goTo(INFORMATIONS_STEP);
			return;
		}

		goPrevious();
	}

	async function handlePrevious(): Promise<void> {
		const canLeave = await canLeaveCurrentStep();

		if (!canLeave) {
			return;
		}

		setAnyChecked(false);
		await navigateTo(previousStep);
	}

	if (isLoading) {
		return (
			<Container>
				<Skeleton active />
			</Container>
		);
	}

	if (isError) {
		return (
			<Container>
				<span>{I18n.get('Oops... Something went wrong!')}</span>
			</Container>
		);
	}

	return (
		<Container>
			<Stepper
				steps={steps}
				currentStep={currentStep}
				reachedStep={furthestStep}
				complete={isComplete}
				consolidated={isLocked}
				onStepClick={handleStepClick}
			/>
			<Form form={form} name={FORM_NAME} autoComplete="off" initialValues={initialValues}>
				{isFirstStep && <Informations />}
				{isStepKeyStep && (
					<StepKeyWrapper>
						<BeraStepKey
							key={currentStepData.id}
							file={file}
							stepId={currentStepData.id}
							stepKeys={currentStepData.step_key}
							stepDescription={currentStepData.description}
							beraReportId={report.id}
							onCheckAnyCollapse={handleAnyCheckedChange}
							onAutoSave={saveStepKey}
							workstation_id={jobSummary?.workstation_id}
							currentBeraStepResultId={currentStepResultId}
						/>
					</StepKeyWrapper>
				)}
			</Form>
			{isResultStep && <Result report={report} onPrevious={handleResultPrevious} onClose={onFinished} />}
			{!isResultStep && (
				<Footer showPrevious={!isFirstStep} saving={isSaving} onPrevious={handlePrevious} onNext={handleNext} />
			)}
		</Container>
	);
}
